import { useCallback, useEffect, useRef, useState } from 'react'
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react'
import { useQueryClient } from '@tanstack/react-query'
import { checkProof, CheckProofDto } from '../api/authApi'
import {
  saveAuthToken,
  removeAuthToken,
  isAuthenticated,
} from '../shared/utils/authToken'
import {
  getStoredPayload,
  removePayload,
} from '../shared/utils/tonProofPayload'
import { useAppContext } from '../contexts/App.context'
import { useTelegram } from './useTelegram'
import { parseStartParam } from '../functions/startParam'
import { onTokenExpired } from '../shared/utils/apiClient'

interface TonProofData {
  proof: {
    timestamp: number
    domain: {
      lengthBytes: number
      value: string
    }
    signature: string
    // NOTE: state_init is NOT in tonProof.proof
    // It comes from wallet.account.walletStateInit
  }
}

interface WalletWithProof {
  connectItems?: {
    tonProof?: TonProofData
  }
}

// Helper to safely access connectItems
function hasConnectItems(
  wallet: unknown,
): wallet is { connectItems?: WalletWithProof['connectItems'] } {
  return (
    typeof wallet === 'object' && wallet !== null && 'connectItems' in wallet
  )
}

interface UseTonProofAuthReturn {
  isAuthenticating: boolean
  isAuthenticated: boolean
  authError: string | null
  authenticate: () => Promise<void>
  referralKey: string | null
}

/**
 * Hook for TON Connect proof-based authentication
 * Handles the full authentication flow:
 * 1. Generate payload from backend
 * 2. Get proof signature from TON Connect wallet
 * 3. Verify proof with backend and receive JWT token
 */
export function useTonProofAuth(): UseTonProofAuthReturn {
  const wallet = useTonWallet()
  const [tonConnectUI] = useTonConnectUI()
  const queryClient = useQueryClient()
  const { tg } = useTelegram()
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authenticated, setAuthenticated] = useState(false)
  const isAuthenticatingRef = useRef(false)
  const { referralKey, setAuthenticated: setContextAuthenticated } =
    useAppContext()

  // Check if already authenticated on mount (only when wallet is connected)
  useEffect(() => {
    const walletAddress = wallet?.account.address
    // Only check auth status if wallet is connected
    // When wallet disconnects, the disconnect effect handles cleanup
    if (walletAddress) {
      const isAuth = isAuthenticated(walletAddress)
      setAuthenticated(isAuth)
      setContextAuthenticated(isAuth)
    }
  }, [wallet?.account.address, setContextAuthenticated])

  const authenticate = useCallback(async () => {
    if (!wallet?.account) {
      setAuthError('Wallet not connected')
      return
    }

    const walletAddress = wallet.account.address

    // Check if already authenticated - if so, no need to re-authenticate
    if (isAuthenticated(walletAddress)) {
      setAuthenticated(true)
      return
    }

    // Prevent multiple simultaneous authentication attempts
    if (isAuthenticatingRef.current) {
      console.log('Authentication already in progress')
      return
    }

    try {
      isAuthenticatingRef.current = true
      setIsAuthenticating(true)
      setAuthError(null)

      const walletPublicKey = wallet.account.publicKey
      const walletStateInit = wallet.account.walletStateInit
      const network = wallet.account.chain.toString()

      if (!walletPublicKey) {
        throw new Error('Wallet public key not available')
      }

      if (!walletStateInit) {
        throw new Error('Wallet state_init not available')
      }

      console.log('Authentication: Starting TON proof authentication...', {
        hasPublicKey: !!walletPublicKey,
        hasStateInit: !!walletStateInit,
      })

      // Step 1: Get stored payload that was used for wallet connection
      // Retry up to 3 times with 500ms delay between attempts
      // This handles race condition on mobile devices where wallet connects
      // before payload is generated
      let storedPayload = getStoredPayload()
      let retries = 0
      const maxRetries = 3

      while (!storedPayload && retries < maxRetries) {
        console.log(
          `Authentication: Payload not found, retrying... (${retries + 1}/${maxRetries})`,
        )
        await new Promise(resolve => setTimeout(resolve, 500))
        storedPayload = getStoredPayload()
        retries++
      }

      if (!storedPayload) {
        throw new Error('No payload found. Please reconnect your wallet.')
      }

      const { payload, payloadToken } = storedPayload
      console.log('Authentication: Using stored payload:', payload)

      // Step 2: Request proof from wallet
      // TON Connect UI doesn't expose a direct way to request proof after connection
      // We need to use the connectItems mechanism or tonProof in the provider
      if (!hasConnectItems(wallet)) {
        throw new Error(
          'Wallet does not support TON proof. Please reconnect your wallet.',
        )
      }

      const connectItems = wallet.connectItems

      if (!connectItems?.tonProof) {
        throw new Error(
          'Wallet does not support TON proof. Please reconnect your wallet.',
        )
      }

      const tonProof = connectItems.tonProof

      console.log('Authentication: TON Proof data received:', {
        timestamp: tonProof.proof.timestamp,
        domain: tonProof.proof.domain,
        hasSignature: !!tonProof.proof.signature,
      })

      // Step 3: Extract Telegram user ID and referral key
      const tgUserId = tg?.initDataUnsafe?.user?.id?.toString()
      const startParam = tg?.initDataUnsafe?.start_param
      const { ref } = parseStartParam(startParam)

      // Step 4: Prepare proof data for backend verification
      // NOTE: state_init comes from wallet.account.walletStateInit, not from tonProof
      const proofData: CheckProofDto = {
        address: walletAddress,
        public_key: walletPublicKey,
        proof: {
          timestamp: tonProof.proof.timestamp,
          domain: {
            lengthBytes: tonProof.proof.domain.lengthBytes,
            value: tonProof.proof.domain.value,
          },
          signature: tonProof.proof.signature,
          payload: payload,
          state_init: walletStateInit, // From wallet.account, not tonProof!
        },
        payloadToken: payloadToken,
        tgUserId: tgUserId,
        referredByKey: ref ?? undefined,
      }

      console.log('Authentication: Sending proof data to backend:', {
        address: proofData.address,
        network: network,
        hasPublicKey: !!proofData.public_key,
        hasProof: !!proofData.proof,
        hasStateInit: !!proofData.proof.state_init,
        stateInitLength: proofData.proof.state_init?.length,
        hasPayloadToken: !!proofData.payloadToken,
        hasTgUserId: !!proofData.tgUserId,
        hasReferredByKey: !!proofData.referredByKey,
      })

      // Step 5: Verify proof with backend and get JWT token
      const authResponse = await checkProof(proofData)
      console.log('Authentication: Authentication successful')

      // Step 6: Save token to localStorage
      saveAuthToken(authResponse.token, walletAddress)

      // Step 7: Update authentication state
      setAuthenticated(true)
      setContextAuthenticated(true)
      console.log('Authentication: State updated, invalidating queries...')

      // Step 8: Invalidate all queries to trigger refetch with new auth state
      // This ensures that queries with enabled: canFetchUserData will now execute
      await queryClient.invalidateQueries()
      console.log('Authentication: Queries invalidated')

      // Step 9: Save referral key (будет автоматически обновлен из профиля в AppContext)
      if (authResponse.user.referralKey) {
        console.log(
          'Authentication: Referral key received:',
          authResponse.user.referralKey,
        )
      }

      // Step 10: Clear used payload
      removePayload()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Authentication failed'
      setAuthError(errorMessage)
      removeAuthToken()
      removePayload()
      setAuthenticated(false)
      setContextAuthenticated(false)
      throw new Error(`Authentication failed: ${errorMessage}`)
    } finally {
      setIsAuthenticating(false)
      isAuthenticatingRef.current = false
    }
  }, [wallet, tg, setContextAuthenticated, queryClient])

  // Auto-authenticate when wallet connects and not already authenticated
  useEffect(() => {
    const walletAddress = wallet?.account.address

    if (walletAddress && !isAuthenticated(walletAddress)) {
      // Longer delay to ensure payload is generated and wallet is fully connected
      // This is especially important for mobile devices where TON Keeper connection
      // can happen very quickly
      const timer = setTimeout(() => {
        authenticate()
      }, 1500)

      return () => clearTimeout(timer)
    }
  }, [wallet?.account.address, authenticate])

  // Clear token when wallet disconnects
  // Use a ref to track if wallet was previously connected
  const wasConnectedRef = useRef(false)

  useEffect(() => {
    if (wallet?.account) {
      // Wallet is connected, mark it
      wasConnectedRef.current = true
    } else if (wasConnectedRef.current) {
      // Wallet WAS connected but now is not - user disconnected
      removeAuthToken()
      removePayload() // Also remove payload so new one is generated on reconnect
      setAuthenticated(false)
      setContextAuthenticated(false)
      setAuthError(null) // Clear auth error on wallet disconnect
      // Cancel all in-flight queries and clear cache to prevent 401 errors
      queryClient.cancelQueries()
      queryClient.clear()
      wasConnectedRef.current = false
    }
    // If wallet was never connected (!wasConnectedRef.current && !wallet?.account),
    // don't remove token - we're still initializing
  }, [wallet?.account, setContextAuthenticated, queryClient])

  // Handle token expiration (401 responses)
  useEffect(() => {
    const unsubscribe = onTokenExpired(() => {
      console.log('Authentication: Token expired, disconnecting wallet')
      removeAuthToken()
      removePayload()
      setAuthenticated(false)
      setContextAuthenticated(false)
      // Cancel all in-flight queries and clear cache to prevent repeated 401 errors
      queryClient.cancelQueries()
      queryClient.clear()
      // setAuthError('Session expired. Please reconnect your wallet.')

      // Disconnect wallet when token expires
      if (wallet?.account) {
        tonConnectUI.disconnect()
      }
    })

    return unsubscribe
  }, [wallet?.account, tonConnectUI, setContextAuthenticated, queryClient])

  return {
    isAuthenticating,
    isAuthenticated: authenticated,
    authError,
    authenticate,
    referralKey,
  }
}
